import { queryOptions } from '@tanstack/react-query';
import { registerBaseURL, registerPartnersBaseURL } from '#constants/hosts';
import { camelizeKeys, client } from '#queries/utils';
import { firstparty as partnerRegisterBase } from '#proto/index.qonto.registration.partner_register';
import { firstparty as registerPartnerRegistration } from '#proto/index.qonto.registration.register.partner_registrations';
import type {
  ApiStakeholder,
  RegistrationJsonApiResponse,
  RegisterPartnersRegistrationResult,
  PartnerRegistrationJsonApiResponse,
} from './types';

const ApiRegistration = partnerRegisterBase.v1.ApiRegistration;
const ApiOrganization = partnerRegisterBase.v1.ApiOrganization;
const ApiPartnerRegistration = registerPartnerRegistration.v1.ApiPartnerRegistration;

export const registerPartnersQueries = {
  all: ['register-partners'] as const,
  registration: (id?: string) =>
    queryOptions({
      queryKey: [...registerPartnersQueries.all, 'registration', id] as const,
      queryFn: async (): Promise<RegisterPartnersRegistrationResult> => {
        if (!id) {
          throw new Error('Registration ID is required');
        }

        const res: RegistrationJsonApiResponse = await client(
          `${registerPartnersBaseURL}/client_api/registrations/${id}?include=organization,stakeholders`
        );

        const registration = ApiRegistration.fromJSON({
          id: res.data.id,
          ...camelizeKeys(res.data.attributes),
          organizationId: res.data.relationships.organization.data.id,
          stakeholderIds: res.data.relationships.stakeholders.data.map(s => s.id),
        });

        const includedItems = res.included ?? [];

        const registrationStakeholders: ApiStakeholder[] = includedItems
          .filter(item => item.type === 'stakeholders')
          .map(item => ({
            ...camelizeKeys(item.attributes),
            id: item.id,
            organizationId: res.data.relationships.organization.data.id,
            registrationId: res.data.id,
          }));

        const organizationId = res.data.relationships.organization.data.id;
        const organizationData = includedItems.find(
          item => item.type === 'organizations' && item.id === organizationId
        );

        if (!organizationData) {
          throw new Error(`Organization ${organizationId} not found in included resources`);
        }

        // Note: organization from ?include=organization does not populate `features`.
        // The Ember hook fetched features via a separate store.findAll('organization') call.
        // If features are needed, make a separate request to GET /client_api/organizations.
        const organization = ApiOrganization.fromJSON({
          id: organizationData.id,
          ...camelizeKeys(organizationData.attributes),
        });

        return {
          registration,
          stakeholders: registrationStakeholders,
          organization,
        };
      },
    }),
  partnerRegistration: (id: string) =>
    queryOptions({
      queryKey: [...registerPartnersQueries.all, 'partner-registration', id] as const,
      queryFn: async () => {
        const json = await client<PartnerRegistrationJsonApiResponse>(
          `${registerBaseURL}/v1/partner_registrations/${id}`
        );

        return ApiPartnerRegistration.fromJSON({
          id: json.data.id,
          ...camelizeKeys(json.data.attributes),
          organizationId: json.data.relationships.organization.data.id,
        });
      },
    }),
};
